import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Drawer,
  IconButton,
  ListItemButton,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import {
  Dashboard,
  Psychology,
  Person,
  Book,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const DRAWER_WIDTH = 240; // Adjusted for better spacing

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const navigationItems = [
    { icon: <Dashboard sx={{ fontSize: 20 }} />, text: 'Dashboard', path: '/dashboard' },
    { icon: <Psychology sx={{ fontSize: 20 }} />, text: 'AI Analysis', path: '/dashboard/analyze' },
    { icon: <Book sx={{ fontSize: 20 }} />, text: 'Wrong Answer Journal', path: '/dashboard/journal' },
    { icon: <Person sx={{ fontSize: 20 }} />, text: 'Profile', path: '/dashboard/profile' },
  ];

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  const handleMouseEnter = () => {
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    setIsExpanded(false);
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const sidebarContent = (
    <Box sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.paper',
      overflow: 'hidden',
    }}>
      {/* Logo */}
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          minHeight: 48,
        }}
      >
        <Psychology 
          sx={{ 
            color: 'text.primary',
            fontSize: 24,
          }} 
        />
        {isExpanded && (
          <Typography 
            variant="subtitle1"
            sx={{ 
              fontWeight: 500,
              color: 'text.primary',
            }}
          >
            Glare
          </Typography>
        )}
      </Box>

      {/* Navigation */}
      <List sx={{ 
        width: '100%',
        p: 1,
        flex: 1,
      }}>
        {navigationItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <Tooltip title={!isExpanded ? item.text : ""} placement="right">
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigate(item.path)}
                sx={{
                  minHeight: 42,
                  borderRadius: 1,
                  px: 2,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  '&.Mui-selected': {
                    bgcolor: 'action.selected',
                    '&:hover': {
                      bgcolor: 'action.selected',
                    },
                  },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    minWidth: 36,
                    color: location.pathname === item.path ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {isExpanded && (
                  <ListItemText 
                    primary={item.text}
                    sx={{
                      m: 0,
                      '& .MuiTypography-root': {
                        fontSize: '0.875rem',
                        fontWeight: location.pathname === item.path ? 500 : 400,
                        color: location.pathname === item.path ? 'text.primary' : 'text.secondary',
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Desktop Sidebar */}
      <Box
        component="nav"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          width: isExpanded ? DRAWER_WIDTH : 56,
          flexShrink: 0,
          transition: 'width 0.2s ease',
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Drawer
          variant="permanent"
          sx={{
            width: isExpanded ? DRAWER_WIDTH : 56,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: isExpanded ? DRAWER_WIDTH : 56,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: 'width 0.2s ease',
              overflow: 'hidden',
              bgcolor: 'background.paper',
              height: '100%',
              position: 'fixed',
            },
          }}
          open={isExpanded}
        >
          {sidebarContent}
        </Drawer>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            bgcolor: 'background.paper',
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          bgcolor: 'background.default',
          pl: { md: `${56}px` },
          width: '100%',
        }}
      >
        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{
              position: 'fixed',
              left: 16,
              top: 16,
              zIndex: 1100,
              color: 'text.secondary',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                bgcolor: 'background.paper',
              },
            }}
          >
            <MenuIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;